<!-- 
*****************************************************************************
**                                                                         **
** Copyright (c) 2012-2024, Tyker Technology BV                            **
** All rights reserved.                                                    **
**                                                                         **
** This software is the confidential and proprietary information of        **
** Tyker Technology BV. Unauthorized copying or disclosure of this file,   **
** via any medium is strictly prohibited. This file is only available to   **
** authorized individuals with the permission of the copyright holders.    **
** If you encounter this file and do not have permission, please contact   ** 
** the copyright holders and delete this file.                             **
**                                                                         **
***************************************************************************** 
-->

<template>
  <v-dialog v-model="dialog" persistent id="projectDetails" width="1200px" :fullscreen="$vuetify.breakpoint.xsOnly">
    <v-card :loading="statusLoading">
      <template slot="progress">
        <v-progress-linear height="10" indeterminate></v-progress-linear>
      </template>
      <v-card-title class="py-6">
        <div class="text-h6 text-sm-h4" style="word-break: break-word">
          {{ name }}
        </div>
        <v-spacer></v-spacer>
        <v-btn icon color="blue darken-1" @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="px-md-6 px-0">
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-container>
            <v-row>
              <v-col class="px-md-3 px-0">
                <v-card class="pa-md-2 pa-0" outlined tile>
                  <v-card-title>{{ language.t("ProjectDetails.projectStatus") }}
                  </v-card-title>
                  <v-card-text>
                    <v-container v-if="!statusLoading">
                      <v-row no-gutters align="center">
                        <v-col class="px-md-4 px-1 text-center text-md-left" :md="3" :cols="4">
                          {{ language.t("ProjectDetails.status") }}:
                          <v-chip :color="projectStatusColor" dark>{{
                            statusString
                            }}</v-chip>
                        </v-col>
                        <v-col class="px-md-4 pa-2 text-right text-md-left" :md="9" :cols="8">
                          <v-btn @click="changeStatus" :loading="statusSaving">{{ statusButton }}
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>


            <v-row v-if="$auth.subscription == '__SUB_CIVIL_ROADPLOTTER'">
              <v-col class="px-md-3 px-0">
                <v-card class="pa-md-2 pa-0" outlined tile>
                  <v-card-title>
                    <v-container class="pa-0">
                      <v-row class="pa-0" no-gutters>
                        <v-col class="pa-2" :md="6" :cols="12">
                          {{ language.t("ProjectDetails.siteCalibration") }}
                        </v-col>
                        <v-col class="pa-2 text-right" :md="6" :cols="12">
                          <v-btn v-if="siteCalibration == null" @click="addCalibrationFile = true" :disabled="loading"
                            color="succes">
                            <v-icon>mdi-plus</v-icon>
                          </v-btn>
                          <v-btn v-else :disabled="loading || !valid" color="accent" @click="updateProjString">
                            <v-icon>mdi-lead-pencil</v-icon>
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-card-title>
                  <v-card-text v-if="siteCalibration != null">
                    <v-container>
                      <v-row>
                        <v-col class="py-0">
                          <v-text-field v-model="projectionName" :label="language.t('ProjectDetails.name')" required
                            dense ></v-text-field>
                        </v-col>
                      </v-row>
                    </v-container>
                    <v-container>
                      <v-row>
                        <v-col class="py-1" cols="4">
                          PROJECTION
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col class="py-0" cols="4">
                          <v-text-field v-model="projectionParameters.lat_0" label="lat_0" required dense
                            :rules="siteCalibrationRules"></v-text-field>
                        </v-col>

                        <v-col class="py-0" cols="4">
                          <v-text-field v-model="projectionParameters.lon_0" label="lon_0" required dense
                            :rules="siteCalibrationRules"></v-text-field>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col class="py-0" cols="4">
                          <v-text-field v-model="projectionParameters.y_0" label="y_0" required dense
                            :rules="siteCalibrationRules"></v-text-field>
                        </v-col>

                        <v-col class="py-0" cols="4">
                          <v-text-field v-model="projectionParameters.x_0" label="x_0" required dense
                            :rules="siteCalibrationRules"></v-text-field>
                        </v-col>

                        <v-col class="py-0" cols="4">
                          <v-text-field v-model="projectionParameters.k_0" label="k_0" required dense
                            :rules="siteCalibrationRules"></v-text-field>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col class="py-1" cols="4">
                          LOC ELLIPSOID
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col class="py-0" cols="4">
                          <v-text-field v-model="projectionParameters.a" label="a" required dense
                            :rules="siteCalibrationRules"></v-text-field>
                        </v-col>

                        <v-col class="py-0" cols="4">
                          <v-text-field v-model="projectionParameters.rf" label="rf" required dense
                            :rules="siteCalibrationRules"></v-text-field>
                        </v-col>

                      </v-row>
                      <v-row>
                        <v-col class="py-1" cols="4">
                          HORZ ADJUST
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col class="py-0" cols="4">
                          <v-text-field v-model="tykerParameters.tyker_px" label="tyker_px" required dense
                            :rules="siteCalibrationRules"></v-text-field>
                        </v-col>

                        <v-col class="py-0" cols="4">
                          <v-text-field v-model="tykerParameters.tyker_py" label="tyker_py" required dense
                            :rules="siteCalibrationRules"></v-text-field>
                        </v-col>

                        <v-col class="py-0" cols="4">
                          <v-text-field v-model="tykerParameters.tyker_x" label="tyker_x" required dense
                            :rules="siteCalibrationRules"></v-text-field>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col class="py-0" cols="4">
                          <v-text-field v-model="tykerParameters.tyker_y" label="tyker_y" required dense
                            :rules="siteCalibrationRules"></v-text-field>
                        </v-col>

                        <v-col class="py-0" cols="4">
                          <v-text-field v-model="tykerParameters.tyker_rz" label="tyker_rz" required dense
                            :rules="siteCalibrationRules"></v-text-field>
                        </v-col>

                        <v-col class="py-0" cols="4">
                          <v-text-field v-model="tykerParameters.tyker_s" label="tyker_s" required dense
                            :rules="siteCalibrationRules"></v-text-field>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>


            <v-row>
              <v-col class="px-md-3 px-0">
                <v-card class="pa-md-2 pa-0" outlined tile>
                  <v-card-title>
                    <v-container class="pa-0">
                      <v-row class="pa-0" no-gutters>
                        <v-col class="pa-2" :md="6" :cols="12">
                          {{ language.t("ProjectDetails.tasksInProject") }}
                        </v-col>
                        <v-col class="pa-2 text-right" :md="6" :cols="12">
                          <v-btn v-if="!$vuetify.breakpoint.xsOnly" @click="removeTaskFromProject()"
                            :disabled="!taskToRemove" color="error">
                            <v-icon>mdi-delete</v-icon>
                            {{ language.t("ProjectDetails.removeTask") }}
                          </v-btn>
                          <v-btn v-else @click="removeTaskFromProject()" :disabled="!taskToRemove" color="error">
                            <v-icon>mdi-delete</v-icon>
                            {{ language.t("App.remove") }}
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-card-title>
                  <v-card-text>
                    <v-data-table v-model="selected" :single-select="singleSelect" @click:row="selectTask"
                      :loading="dataTableLoading" :headers="headers" :items="projectTasks" :items-per-page="5"
                      item-key="taskId" :footer-props="{
                        itemsPerPageText: language.t(
                          'DataTables.itemsPerPageText'
                        ),
                        itemsPerPageAllText: language.t(
                          'DataTables.itemsPerPageAllText'
                        ),
                      }">
                      <template v-slot:[`item.status`]="{ item }">
                        <v-chip :color="getTaskStatusColor(item.status)" dark>
                          {{ item.status }}
                        </v-chip>
                      </template>
                      <template v-slot:[`footer.page-text`]="items">
                        {{ items.pageStart }} - {{ items.pageStop }}
                        {{ language.t("DataTables.pageText") }}
                        {{ items.itemsLength }}
                      </template>
                    </v-data-table>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
            <v-row>
              <v-col class="px-md-3 px-0">
                <v-card class="pa-md-2 pa-0" outlined tile>
                  <v-card-title>{{ language.t("ProjectDetails.addTasks") }}
                  </v-card-title>
                  <v-card-text>
                    <v-container class="pa-0">
                      <v-row class="pa-0" no-gutters>
                        <v-col class="px-4 text-center text-md-left" :md="3" :cols="12">
                          <v-select v-model="selectedTask" dense solo :items="selectableTasks" item-text="name"
                            item-value="taskId" :loading="tasksLoading">
                          </v-select>
                        </v-col>
                        <v-col class="px-4 text-right text-md-left" :md="9" :cols="12">
                          <v-btn v-if="!$vuetify.breakpoint.xsOnly" @click="addTaskToProject()"
                            :disabled="!selectedTask" color="success">
                            <v-icon>mdi-plus</v-icon>{{ language.t("ProjectDetails.addToProject") }}
                          </v-btn>
                          <v-btn v-else @click="addTaskToProject()" :disabled="!selectedTask" color="success">
                            <v-icon>mdi-plus</v-icon>{{ language.t("App.add") }}
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>
      <v-card-actions class="card-actions">
        <v-spacer></v-spacer>
        <v-btn color="blue darken-1" text @click="$emit('close')">
          <v-icon>mdi-close</v-icon>{{ language.t("App.close") }}
        </v-btn>
      </v-card-actions>
    </v-card>
    <SiteCalibration v-if="addCalibrationFile" :projectId="projectId" v-on:close="addCalibrationFile = false"
      v-on:save="saveProjString" />
  </v-dialog>
</template>

<script>
import moment from "moment";
import SiteCalibration from "./SiteCalibration"

export default {
  components: {
    SiteCalibration
  },
  props: { projectId: null },
  data() {
    return {
      dialog: true,
      error: null,
      success: false,
      projectStatusColor: "",
      taskStatusColor: "",
      statusButton: "",
      siteCalibration: null,
      projectionName: "",
      projectionParameters: {
        proj: null,
        lat_0: null,
        lon_0: null,
        k_0: null,
        x_0: null,
        y_0: null,
        a: null,
        rf: null,
      },
      tykerParameters: {
        tyker_x: null,
        tyker_px: null,
        tyker_y: null,
        tyker_py: null,
        tyker_s: null,
        tyker_rz: null,
      },

      singleSelect: true,
      selected: [],
      addCalibrationFile: false,

      loading: false,
      statusLoading: false,
      selectableTasksLoading: false,
      tasksLoading: false,
      dataTableLoading: false,

      statusSaving: false,

      customerId: null,
      name: "",
      status: "",
      statusString: "",
      siteCalibrationRules: [(v) => (!isNaN(v) && v !== null && v !== undefined) || "valid value required"],
      valid: true,

      allTasks: [],
      selectableTasks: [],
      projectTasks: [],
      headers: [
        { text: this.language.t("ProjectDetails.name"), value: "name" },
        {
          text: this.language.t("ProjectDetails.creationData"),
          value: "creationData",
        },
        { text: this.language.t("ProjectDetails.status"), value: "status" },
      ],

      selectedTask: null,
      taskToRemove: null,
    };
  },
  mounted: function () {
    this.getProject();
    this.getTasks();
  },
  computed: {},
  watch: {
    status(val, oldVal) {
      this.statusString = this.getStatus();
      this.projectStatusColor = val === "1" ? "green" : "blue";
      this.statusButton =
        val === "1"
          ? this.language.t("ProjectDetails.changeStatusCompleted")
          : this.language.t("ProjectDetails.changeStatusActive");
    },
    projectionParameters: {
      handler(val) {
        console.log(val)
      },
      deep: true,
    },
    tykerParameters: {
      handler(val) {
        console.log(val)
      },
      deep: true,
    },
  },
  methods: {
    updateProjString() {
      var projString = "+proj=" + this.projectionParameters.proj +
        " +lat_0=" + this.projectionParameters.lat_0 +
        " +lon_0=" + this.projectionParameters.lon_0 +
        " +k_0=" + this.projectionParameters.k_0 +
        " +x_0=" + this.projectionParameters.x_0 +
        " +y_0=" + this.projectionParameters.y_0 +
        " +a_0=" + this.projectionParameters.a +
        " +rf_0=" + this.projectionParameters.rf +
        " +units=m"
      var tykerProjString =
        "+tyker_rz=" + this.tykerParameters.tyker_rz +
        " +tyker_s=" + this.tykerParameters.tyker_s +
        " +tyker_x=" + this.tykerParameters.tyker_x +
        " +tyker_y=" + this.tykerParameters.tyker_y +
        " +tyker_px=" + this.tykerParameters.tyker_px +
        " +tyker_py=" + this.tykerParameters.tyker_py
      var completeProjString = projString + " " + tykerProjString

      this.saveProjString(completeProjString, this.projectionName, this.projectId)
    },
    saveProjString(projString, name, projectId) {
      this.$axios.post(route("api.projection.save"), { projString: projString, name: name, projectId: projectId }).then((res) => {
        this.getProject();
        this.$emit("update");
        this.addCalibrationFile = false;
      });
    },
    getTaskStatusColor(taskStatus) {
      switch (taskStatus) {
        case this.language.t("Tasklist.taskStatusRemoved"):
          return "red";
        case this.language.t("Tasklist.taskStatusNew"):
          return "green";
        case this.language.t("Tasklist.taskStatusCompleted"):
          return "blue";
        default:
          return "grey";
      }
    },
    selectTask(item, row) {
      let selectState = row.isSelected ? false : true;
      row.select(selectState);

      if (selectState) {
        this.taskToRemove = item.taskId;
      } else {
        this.taskToRemove = null;
      }
    },
    getStatus() {
      return this.status === "1"
        ? this.language.t("ProjectDetails.statusActive")
        : this.language.t("ProjectDetails.statusCompleted");
    },
    getTaskStatus(taskStatus) {
      switch (taskStatus) {
        case "__TASKSTATUSTYPE_REMOVED":
          return this.language.t("Tasklist.taskStatusRemoved");
        case "__TASKSTATUSTYPE_NEW":
          return this.language.t("Tasklist.taskStatusNew");
        case "__TASKSTATUSTYPE_DONE":
          return this.language.t("Tasklist.taskStatusCompleted");
        default:
          return "Onbekende status";
      }
    },
    changeStatus() {
      var newStatus = this.status === "1" ? 0 : 1;
      this.statusSaving = true;
      this.$axios
        .post(route("api.projects.status", [this.projectId, newStatus]))
        .then((res) => {
          this.getProject();
          this.$emit("update");
        });
    },
    getProject() {
      this.statusLoading = this.status === "" && true;
      this.loading = true

      this.$axios.get(route("api.projects.get", this.projectId)).then((res) => {
        console.log(res)
        this.customerId = res.data.customer_customerId;
        this.name = res.data.name;
        this.status = res.data.active; // '1' for active '0' for completed
        this.siteCalibration = res.data.projection;
        this.statusSaving = false;
        this.statusLoading = false;

        const getParam = (key, projitems, defaultValue = 0) => {
          const item = projitems.find(s => s.startsWith(key + "="))
          return item ? parseFloat(item.split("=")[1]) : defaultValue
        }

        if (this.siteCalibration) {
          this.projectionName = res.data.projection.name

          var projstring = res.data.projection.def
          var projitems = projstring.split(" ")
          console.log(projitems)
          this.projectionParameters = {
            proj: projitems.find(s => s.includes("+proj")).split("=")[1],
            lat_0: getParam("+lat_0", projitems),
            lon_0: getParam("+lon_0", projitems),
            k_0: getParam("+k_0", projitems),
            x_0: getParam("+x_0", projitems),
            y_0: getParam("+y_0", projitems),
            a: getParam("+a_0", projitems),
            rf: getParam("+rf_0", projitems),
          }
          this.tykerParameters = {
            tyker_x: getParam("+tyker_x", projitems),
            tyker_px: getParam("+tyker_px", projitems),
            tyker_y: getParam("+tyker_y", projitems),
            tyker_py: getParam("+tyker_py", projitems),
            tyker_s: getParam("+tyker_s", projitems),
            tyker_rz: getParam("+tyker_rz", projitems),
          }
        }
        this.loading = false;
      });
    },
    getTasks() {
      this.allTasks = [];
      this.dataTableLoading = true;
      this.tasksLoading = true;

      this.$axios.get(route("api.tasks")).then((res) => {
        this.allTasks = res.data;
        this.tasksLoading = false;
        this.getProjectTasks();
        this.getSelectableTasks();
      });
    },
    getProjectTasks() {
      this.projectTasks = [];
      for (let i = 0; i < this.allTasks.length; i++) {
        if (this.allTasks[i].project?.projectId === this.projectId)
          this.projectTasks.push({
            taskId: this.allTasks[i].taskId,
            name: this.allTasks[i].name,
            creationData: this.allTasks[i].created,
            unix: moment.utc(this.allTasks[i].created).format("X"),
            status: this.getTaskStatus(this.allTasks[i].status),
          });
      }

      this.projectTasks.sort((a, b) => b.unix - a.unix);
      this.dataTableLoading = false;
    },
    getSelectableTasks() {
      this.selectableTasksLoading;
      this.selectableTasks = [];
      for (let i = 0; i < this.allTasks.length; i++) {
        if (
          !this.allTasks[i].project ||
          this.allTasks[i].project.projectId != this.projectId
        )
          this.selectableTasks.push(this.allTasks[i]);
      }
      this.selectableTasks.reverse();
      this.dataTableLoading = false;
    },
    addTaskToProject() {
      this.loading = true;

      this.$axios
        .post(
          route("api.tasks.projects.add", [this.projectId, this.selectedTask])
        )
        .then((res) => {
          console.log(res);
          this.loading = false;
          this.selectedTask = null;
        });
      this.getTasks();
    },
    removeTaskFromProject() {
      this.loading = true;

      this.$axios
        .post(route("api.tasks.projects.remove", this.taskToRemove))
        .then((res) => {
          console.log(res);
          this.loading = false;
          this.taskToRemove = null;
        });
      this.getTasks();
    },
    onClose() {
      this.$emit("close");
    },
  },
};
</script>
<style scoped></style>
